import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Building2, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://emergentagent-images.s3.amazonaws.com/generated/7d35c71b-80b4-4f68-92d7-9e5fdc70c7bc.png";

export default function BetaRequestPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    portfolio_size: '',
    use_case: '',
    referral_source: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/waitlist/beta-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Beta request submitted successfully!');
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Beta request error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f1419] to-[#0a0a0f] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're on the list!</h1>
          <p className="text-zinc-400 mb-8">
            Thank you for your interest in Insite360. We'll review your application and reach out shortly with next steps.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#0f1419] to-[#0a0a0f]">
      {/* Header */}
      <header className="p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <img src={LOGO_URL} alt="Insite360" className="h-10 w-auto" />
        </div>
      </header>

      {/* Form */}
      <main className="p-6 pb-20">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-teal-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Request Beta Access</h1>
            <p className="text-zinc-400">
              Join our exclusive beta program and be among the first to experience Insite360's powerful compliance monitoring platform.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="glass-card rounded-xl p-6 space-y-5">
              {/* Name */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Full Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                  required
                  data-testid="beta-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Work Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                  required
                  data-testid="beta-email-input"
                />
              </div>

              {/* Company */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Company *</Label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="ABC Property Management"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                  required
                  data-testid="beta-company-input"
                />
              </div>

              {/* Role */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Your Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="beta-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="building_owner">Building Owner</SelectItem>
                    <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                    <SelectItem value="developer">Real Estate Developer</SelectItem>
                    <SelectItem value="attorney">Real Estate Attorney</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Portfolio Size */}
              <div>
                <Label className="text-zinc-300 mb-2 block">Portfolio Size</Label>
                <Select 
                  value={formData.portfolio_size} 
                  onValueChange={(val) => setFormData({ ...formData, portfolio_size: val })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="beta-portfolio-select">
                    <SelectValue placeholder="How many properties do you manage?" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="1-5">1-5 properties</SelectItem>
                    <SelectItem value="6-20">6-20 properties</SelectItem>
                    <SelectItem value="21-50">21-50 properties</SelectItem>
                    <SelectItem value="51-100">51-100 properties</SelectItem>
                    <SelectItem value="100+">100+ properties</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Use Case */}
              <div>
                <Label className="text-zinc-300 mb-2 block">How do you plan to use Insite360?</Label>
                <Textarea
                  value={formData.use_case}
                  onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                  placeholder="Tell us about your compliance monitoring needs..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 min-h-[100px]"
                  data-testid="beta-usecase-input"
                />
              </div>

              {/* Referral Source */}
              <div>
                <Label className="text-zinc-300 mb-2 block">How did you hear about us?</Label>
                <Select 
                  value={formData.referral_source} 
                  onValueChange={(val) => setFormData({ ...formData, referral_source: val })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="beta-referral-select">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="google">Google Search</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="referral">Referral from colleague</SelectItem>
                    <SelectItem value="event">Industry event</SelectItem>
                    <SelectItem value="news">News article</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg"
              data-testid="beta-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Request Beta Access'
              )}
            </Button>

            <p className="text-center text-zinc-500 text-sm">
              By submitting, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
